<template>
  <div class="pa-2">
    <div class="d-flex flex-column custom-container">
      <v-row class="flex-grow-0 mt-1 align-center justify-space-between">
        <span v-if="jobDefinitionToInitiate.length > 0">
          <v-menu class="text" max-width="20%" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" v-bind="attrs" v-on="on">
                <v-icon left>mdi-plus</v-icon>
                {{ $t('new_job') }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-for="jobDefinition in jobDefinitionToInitiate" :key="jobDefinition.job_definition_id"
                @click="openJobModal(jobDefinition.job_definition_id)">
                <v-list-item-content>
                  <v-list-item-title><v-icon left>{{ jobDefinition.icon }}</v-icon>{{ jobDefinition.title
                    }}</v-list-item-title>

                  <v-list-item-subtitle class="overflow-visible text-description">{{ jobDefinition.description
                    }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn class="ml-4" color="primary" outlined @click="goToCreateCampaign">
            {{ $t('campaigns') }}
          </v-btn>
        </span>

        <span v-if="$vuetify.breakpoint.mdAndUp" class="headline">
          <v-icon left>mdi-sticker-check-outline</v-icon>
          {{ $t('jobs_page_title') }}
        </span>

        <span class="d-flex align-center">
          <v-btn v-if="view == 'timeline' && isTimelineViewAllowed" @click="goToNow" color="primary" outlined rounded>
            {{ $t('today') }}
          </v-btn>

          <v-select v-if="view == 'timeline' && isTimelineViewAllowed" class="ml-4 d-inline-flex timeline-select-zoom"
            v-model="selectedTimelineInterval" :items="timelineIntervalOptions" item-text="title" item-value="id"
            hide-details dense outlined rounded></v-select>

          <v-btn-toggle v-if="isListViewAllowed || isTimelineViewAllowed" v-model="view" class="ml-4" dense mandatory>
            <v-btn v-if="isListViewAllowed" value="list">
              <v-icon>mdi-format-list-bulleted</v-icon>
            </v-btn>
            <v-btn v-if="isTimelineViewAllowed" value="timeline">
              <v-icon>mdi-grid-large</v-icon>
            </v-btn>
          </v-btn-toggle>
        </span>
      </v-row>

      <v-row class="mt-6">
        <v-card class="job-filters" dense outlined width="100%">
          <div class="filter-row primary-filter-row">
            <div class="filter-control filter-control-search">
              <v-autocomplete v-if="view == 'timeline'" v-model="selectedSearchedJob" :items="jobsSearchedOptions"
                :loading="isSearchingJob" :search-input.sync="searchJobTerm" :no-filter="true" hide-no-data hide-selected
                hide-details :item-text="formatSearchedJob" item-value="job_id" :label="$t('search_job_label')"
                :placeholder="$t('search_job_label')" return-object outlined dense clearable></v-autocomplete>

              <v-text-field v-else-if="view == 'list'" v-model="searchJobTerm" :label="$t('search')"
                :hint="$t('search_min_length_hint', { n: minSearchLength })" :persistent-hint="isListSearchTermTooShort"
                :hide-details="!isListSearchTermTooShort"
                :error="isListSearchTermTooShort" @keydown.enter="triggerListSearch"
                @click:append="triggerListSearch" @click:clear="searchTermEvent = ''" append-icon="mdi-magnify"
                clearable outlined dense></v-text-field>
            </div>

            <div class="filter-control">
              <v-text-field v-model.trim="selectedJobCustomerCode" :label="$t('job_customer_code_short')"
                clearable hide-details outlined dense></v-text-field>
            </div>

            <div class="filter-control">
              <v-select v-model="selectedPunctuality" :items="punctualityOptions" item-text="title" item-value="value"
                :label="$t('punctuality')" clearable chips deletable-chips small-chips dense outlined hide-details
                multiple>
                <template v-slot:item="{ item }">
                  <span><v-icon left :color="item.color">mdi-circle</v-icon>{{ $t(item.title) }}</span>
                </template>

                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0" class="filter-display">
                    <v-chip :color="item.color" small>
                      <span>{{ $t(item.title) }}</span>
                    </v-chip>
                    <span v-if="selectedPunctuality.length > 1" class="grey--text text-caption">
                      (+{{ selectedPunctuality.length - 1 }} {{ $t('others') }})
                    </span>
                  </span>
                </template>
              </v-select>
            </div>

            <div class="filter-control">
              <v-select v-model="selectedStatusGroups" :items="statusesOptions" item-text="title" item-value="value"
                :label="$t('job_status')" clearable chips small-chips dense outlined hide-details single-line multiple>
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0" class="filter-display">
                    <v-chip small>
                      <span>{{ item.title }}</span>
                    </v-chip>
                    <span v-if="selectedStatusGroups.length > 1" class="grey--text text-caption">
                      (+{{ selectedStatusGroups.length - 1 }} {{ $t('others') }})
                    </span>
                  </span>
                </template>
              </v-select>
            </div>

            <div class="filter-actions">
              <v-btn class="more-filters-btn" text small @click="filtersExpanded = !filtersExpanded">
                {{ $t('more_filters') }}
                <span v-if="moreFiltersActiveCount > 0" class="more-filters-badge">
                  {{ moreFiltersActiveCount }}
                </span>
                <v-icon right small>{{ filtersExpanded ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
              </v-btn>
            </div>
          </div>

          <v-expand-transition>
            <div v-show="filtersExpanded" class="filter-row advanced-filter-row">
              <div class="filter-control">
                <v-select v-model="selectedCampaign" :items="campaigns" item-text="campaign_title" item-value="campaign_id"
                  :label="$t('campaigns')" clearable chips small-chips dense outlined hide-details multiple>
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0" class="filter-display">
                      <v-chip small>
                        <span>{{ item.campaign_title }}</span>
                      </v-chip>
                      <span v-if="selectedCampaign.length > 1" class="grey--text text-caption">
                        (+{{ selectedCampaign.length - 1 }} {{ $t('others') }})
                      </span>
                    </span>
                  </template>
                </v-select>
              </div>

              <div class="filter-control">
                <v-select v-model="selectedCustomer" :items="customers" item-text="customer_name" item-value="customer_id"
                  :label="$t('customer')" clearable chips small-chips dense outlined hide-details multiple>
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0" class="filter-display">
                      <v-chip small>
                        <span>{{ item.customer_name }}</span>
                      </v-chip>
                      <span v-if="selectedCustomer.length > 1" class="grey--text text-caption">
                        (+{{ selectedCustomer.length - 1 }} {{ $t('others') }})
                      </span>
                    </span>
                  </template>
                </v-select>
              </div>

              <div class="filter-control">
                <v-autocomplete v-model="selectedRequester" :items="sortedUsers" item-text="name" item-value="users_id"
                  :label="$t('submitter')" clearable chips small-chips dense outlined hide-details multiple>
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0" class="filter-display">
                      <v-chip small>
                        <span>{{ item.name }}</span>
                      </v-chip>
                      <span v-if="selectedRequester.length > 1" class="grey--text text-caption">
                        (+{{ selectedRequester.length - 1 }} {{ $t('others') }})
                      </span>
                    </span>
                  </template>
                </v-autocomplete>
              </div>

              <div class="filter-control">
                <v-select v-model="selectedPool" :items="pools" item-text="pool_title" item-value="pool_id"
                  :label="$t('pool')" clearable chips small-chips dense outlined hide-details multiple>
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0" class="filter-display">
                      <v-chip small>
                        <span>{{ item.pool_title }}</span>
                      </v-chip>
                      <span v-if="selectedPool.length > 1" class="grey--text text-caption">
                        (+{{ selectedPool.length - 1 }} {{ $t('others') }})
                      </span>
                    </span>
                  </template>
                </v-select>
              </div>

              <div class="filter-control">
                <v-select v-model="selectedLane" :items="laneFilterOptions" item-text="filter_title" item-value="lane_id"
                  :label="$t('lane')" clearable chips small-chips dense outlined hide-details multiple>
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0" class="filter-display">
                      <v-chip small>
                        <span>{{ item.lane_title }}</span>
                      </v-chip>
                      <span v-if="selectedLane.length > 1" class="grey--text text-caption">
                        (+{{ selectedLane.length - 1 }} {{ $t('others') }})
                      </span>
                    </span>
                  </template>
                </v-select>
              </div>

              <div class="filter-control d-flex align-center">
                <v-switch v-model="selectedShowFinished" :label="$t('show_finished')" hide-details dense
                  class="mt-0 pt-0"></v-switch>
              </div>

              <div class="filter-clear-control">
                <v-btn @click="clearFilters" class="clear-filters-btn" text small :disabled="!hasActiveFilter">
                  <v-icon left small>mdi-filter-remove-outline</v-icon>
                  {{ $t('clear_filters') }}
                </v-btn>
              </div>
            </div>
          </v-expand-transition>
        </v-card>
      </v-row>

      <v-row class="mt-6">
        <v-card v-if="isTimelineViewAllowed && view == 'timeline'" :loading="loading" class="timeline-container align-center"
          width="-webkit-fill-available" dense outlined rounded>
          <JobTimelineView @view-job="viewJob"
            :timeline-view-mode="timelineIntervalOptions[selectedTimelineInterval].viewMode"
            :columns-count="timelineIntervalOptions[selectedTimelineInterval].colCount" :groups="groups" :users="users"
            :statuses="statuses" :lanes="lanes" :user-lanes="userLanes" :pools="pools" :job-definitions="jobDefinitions"
            :user-acls="userAcls" :appConfig="appConfig" :filter-campaign="selectedCampaign"
            :filter-customer="selectedCustomer" :filter-job-customer-code="selectedJobCustomerCode"
            :filter-requester="selectedRequester" :filter-pool="selectedPool" :filter-lane="selectedLane"
            :filterStatus="selectedStatusIds" :filter-punctuality="selectedPunctuality" :filter-jobs="filterJobs"
            :filter-show-finished="selectedShowFinished"
            ref="timelineView" />
        </v-card>

        <v-container v-else-if="isListViewAllowed && view == 'list'" class="pa-0" fluid>
          <JobListView :key="`job-list-${view}`" @view-job="viewJob"
            :timeline-view-mode="timelineIntervalOptions[selectedTimelineInterval].viewMode"
            :columns-count="timelineIntervalOptions[selectedTimelineInterval].colCount" :groups="groups" :users="users"

            :statuses="statuses" :lanes="lanes" :user-lanes="userLanes" :pools="pools" :job-definitions="jobDefinitions"
            :user-acls="userAcls" :appConfig="appConfig" :filter-campaign="selectedCampaign"
            :filter-customer="selectedCustomer" :filter-job-customer-code="selectedJobCustomerCode"
            :filter-requester="selectedRequester" :filter-pool="selectedPool" :filter-lane="selectedLane"
            :campaigns="campaigns" :customers="customers"
            :filterStatus="selectedStatusIds" :filter-punctuality="selectedPunctuality"
            :filter-show-finished="selectedShowFinished"
            :search-job-event="searchTermEvent" />
        </v-container>
      </v-row>

      <v-hover v-if="isAdmin" v-slot="{ hover }">
        <v-btn @click="goToSettings" color="primary" absolute bottom right class="mb-6 pa-2" min-width="0" rounded>
          <v-icon>mdi-cog-outline</v-icon>
          <v-expand-x-transition>
            <span v-show="hover" class="transition-ease-in-ease-out mx-2">{{ $t('settings') }}</span>
          </v-expand-x-transition>
        </v-btn>
      </v-hover>
    </div>

    <!-- Job Modal Component -->
    <Job ref="jobModal" :job-id="selectedJobId" :groups="groups" :users="users" :statuses="statuses" :pools="pools"
      :pools-statuses="poolsStatuses" :lanes="lanes" :user-lanes="userLanes" :user-acls="userAcls"
      :campaigns="campaigns" :job-definitions="jobDefinitions" @job-created="onJobCreated" @job-updated="onJobUpdated"
      @job-deleted="onJobDeleted" />
  </div>
</template>

<style scoped>
.custom-container {
  height: 100%;
  width: 100%;
}

.text-description {
  white-space: break-spaces !important;
}

.timeline-container {
  width: 100%;
}

.filter-display {
  display: flex;
  overflow: hidden;
  text-wrap: nowrap;
  max-width: -webkit-fill-available;
  align-items: center;
}

.job-filters {
  border-color: #d6d9dd !important;
  border-radius: 6px !important;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 10px;
}

.filter-row {
  align-items: center;
  display: flex;
  gap: 14px;
  width: 100%;
}

.filter-control {
  flex: 1 1 0;
  min-width: 0;
}

.filter-control-search {
  flex-basis: 300px;
  flex-grow: 1.1;
}

.filter-actions {
  display: flex;
  flex: 0 0 142px;
  justify-content: center;
}

.more-filters-btn {
  color: #00b83f !important;
  font-size: 12px !important;
  font-weight: 700;
  letter-spacing: 0 !important;
  min-width: 136px !important;
  padding: 0 6px !important;
}

.more-filters-badge {
  align-items: center;
  background: #00c853;
  border-radius: 50%;
  color: #fff;
  display: inline-flex;
  font-size: 10px;
  font-weight: 700;
  height: 16px;
  justify-content: center;
  line-height: 16px;
  margin-left: 8px;
  min-width: 16px;
  padding: 0 4px;
}

.filter-clear-control {
  display: flex;
  flex: 0 0 92px;
  justify-content: center;
}

.clear-filters-btn {
  color: #5f6368 !important;
  font-size: 12px !important;
  font-weight: 700;
  letter-spacing: 0 !important;
  min-width: 0 !important;
  padding: 0 2px !important;
}

@media (max-width: 1260px) {
  .filter-row {
    flex-wrap: wrap;
  }

  .filter-control {
    flex-basis: calc(50% - 7px);
  }

  .filter-control-search {
    flex-basis: 100%;
  }
}

@media (max-width: 720px) {
  .filter-control,
  .filter-actions,
  .filter-clear-control {
    flex-basis: 100%;
  }

  .filter-actions,
  .filter-clear-control {
    justify-content: flex-start;
  }
}

.timeline-select-zoom {
  width: 150px;
}
</style>

<script>

import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import router from "@/router"

import UserRoles from '@/store/core/user/user-roles'
import Job from '@/views/job/job/Job.vue'
import JobTimelineView from '@/components/job/JobTimelineView.vue'
import JobListView from '@/components/job/JobListView.vue'

import StatusAPI from '@/services/api/job/status'
import PoolAPI from '@/services/api/job/pool'
import PoolStatusAPI from '@/services/api/job/poolstatus'
import PoolACLAPI from '@/services/api/job/pool_acl'
import LaneAPI from '@/services/api/job/lane'
import LaneExecutorAPI from '@/services/api/job/lane_executor'
import CampaignAPI from '@/services/api/job/campaign'
import JobDefinitionAPI from '@/services/api/job/job_definition'
import JobInitiatorAPI from '@/services/api/job/job_initiator'
import UserACLAPI from '@/services/api/job/user_acl'
import CustomerAPI from '@/services/api/business/customer'

import GroupActions from '@/store/core/group/actions-types'
import UserActions from '@/store/core/user/actions-types'
import JobActions from '@/store/job/actions-types'
import JobMutations from '@/store/job/mutations-types'
import AppMutations from '@/store/app/mutations-types'

const VIEW_LIST_OFF = 1
const VIEW_TIMELINE_OFF = 2

export default {
  name: "JobHome",

  components: {
    Job,
    JobTimelineView,
    JobListView,
  },

  data() {
    return {
      loading: false,
      view: 'timeline',
      groups: [],
      users: [],
      statuses: [],
      pools: [],
      poolsStatuses: [],
      lanes: [],
      userLanes: [],
      campaigns: [],
      customers: [],
      jobDefinitions: [],
      jobsInitiator: [],
      options: {},
      userAcls: [],
      userViewAcls: 0,
      filtersExpanded: false,
      selectedJobId: null,
      selectedCampaign: [],
      selectedCustomer: [],
      selectedRequester: [],
      selectedPool: [],
      selectedLane: [],
      selectedJobCustomerCode: "",
      selectedStatus: [],
      selectedPunctuality: [],
      selectedShowFinished: false,
      punctualityOptions: [
        {
          title: 'job_waiting',
          color: '#b0b5bf',
          value: 'job-waiting'
        },
        {
          title: 'job_done',
          color: '#9bd5b7',
          value: 'job-done'
        },
        {
          title: 'job_doing',
          color: '#f5ba54',
          value: 'job-doing'
        },
        {
          title: 'job_start_late',
          color: '#f99d6d',
          value: 'job-start-date-late'
        },
        {
          title: 'job_end_late',
          color: '#ef898a',
          value: 'job-end-date-late'
        },
        {
          title: 'job_deadline_late',
          color: '#c68182',
          value: 'job-deadline-late'
        },
        {
          title: 'job_canceled',
          color: '#ab8cf1',
          value: 'job-canceled'
        },
      ],
      searchJobTerm: "",
      searchTermEvent: "",
      selectedSearchedJob: null,
      jobsSearched: [],
      isSearchingJob: false,
      searchDebounceTimer: null,
      searchRequestId: 0,
      minSearchLength: 3,
      filterJobs: [],
      selectedTimelineInterval: 0,
      timelineIntervalOptions: [
        { id: 0, title: this.$t('n_day', { n: 1 }), viewMode: "hours", colCount: 16 },
        { id: 1, title: this.$t('n_days', { n: 2 }), viewMode: "hours", colCount: 24 },
        { id: 2, title: this.$t('n_days', { n: 3 }), viewMode: "hours", colCount: 32 },
        { id: 3, title: this.$t('n_week', { n: 1 }), viewMode: "days", colCount: 14 },
        { id: 4, title: this.$t('n_weeks', { n: 2 }), viewMode: "days", colCount: 21 },
        { id: 5, title: this.$t('n_weeks', { n: 3 }), viewMode: "days", colCount: 28 },
      ],
    }
  },

  mounted() {
    this.loadFiltersFromURL()
    this.filtersExpanded = this.moreFiltersActiveCount > 0

    // Customer data is served by the Business application. Match the
    // configuration-based selection used by the Job Campaign screen before
    // any Job Home request can invoke AxiosBusiness.
    const businessAppId = this.appConfig.business_app_inst_id || this.appConfig.business_app_id
    if (businessAppId) {
      this[AppMutations.SET_SELECTED_BUSINESS_APP](businessAppId)
    }

    this.fetchData()
    this.ensureContentAppInRoute()
  },

  computed: {
    ...mapGetters("app", {
      getUserApplicationById: "getUserApplicationById"
    }),

    ...mapState({
      userRoles: state => state.core.auth.userRoles,
      userData: state => state.core.auth.userData,
    }),

    ...mapState('app', {
      selectedJobApp: state => state.selectedJobApp,
    }),

    isAdmin() {
      return this.userRoles.includes(UserRoles.USER_ADMIN)
    },

    appConfig() {
      const agent = this.getUserApplicationById(this.selectedJobApp)

      return this.parseJson(agent.config)
    },

    statusesOptions() {
      const options = new Map()

      this.pools.forEach((pool) => {
        const poolStatuses = pool.statuses || []
        poolStatuses.forEach((ps) => {
          const status = this.getStatusById(ps.status_id)

          if (!status) return

          const title = status.status_title
          if (!options.has(title)) options.set(title, new Set())
          options.get(title).add(Number(status.status_id))
        })
      })

      return [...options.entries()]
        .map(([title, ids]) => ({
          title,
          value: [...ids].sort((a, b) => a - b).join(',')
        }))
        .sort((a, b) => a.title.localeCompare(b.title))
    },

    selectedStatusGroups: {
      get() {
        return this.statusesOptions
          .filter(option => option.value.split(',').some(id => this.selectedStatus.includes(Number(id))))
          .map(option => option.value)
      },
      set(groups) {
        this.selectedStatus = [...new Set(groups.flatMap(value => value.split(',').map(Number)))]
      }
    },

    selectedStatusIds() {
      // Expand restored URL selections to include every ID with the same name.
      return [...new Set([
        ...this.selectedStatus,
        ...this.selectedStatusGroups.flatMap(value => value.split(',').map(Number))
      ])]
    },

    laneFilterOptions() {
      const selectedPoolIds = this.selectedPool.map(poolId => String(poolId))

      return this.lanes
        .filter((lane) => {
          const lanePoolId = lane.pool?.pool_id ?? lane.pool_id

          return selectedPoolIds.length === 0 || selectedPoolIds.includes(String(lanePoolId))
        })
        .map((lane) => {
          const poolTitle = lane.pool?.pool_title

          return {
            ...lane,
            filter_title: poolTitle ? `${lane.lane_title} - ${poolTitle}` : lane.lane_title
          }
        })
    },

    jobDefinitionToInitiate() {
      const initiatorIds = this.jobsInitiator.map(id => String(id))

      return this.jobDefinitions.filter(jd => initiatorIds.includes(String(jd.job_definition_id)))
    },

    isListViewAllowed() {
      return (this.userViewAcls & VIEW_LIST_OFF) === 0
    },

    isTimelineViewAllowed() {
      return (this.userViewAcls & VIEW_TIMELINE_OFF) === 0
    },

    hasActiveFilter() {
      return this.selectedSearchedJob
        || this.filterJobs.length > 0
        || this.selectedCampaign.length > 0
        || this.selectedCustomer.length > 0
        || this.selectedRequester.length > 0
        || this.selectedPool.length > 0
        || this.selectedLane.length > 0
        || !!this.selectedJobCustomerCode
        || this.selectedStatus.length > 0
        || this.selectedPunctuality.length > 0
        || this.selectedShowFinished
    },

    moreFiltersActiveCount() {
      const listFiltersCount = [
        this.selectedCampaign,
        this.selectedCustomer,
        this.selectedRequester,
        this.selectedPool,
        this.selectedLane
      ].reduce((count, filterValue) => count + (filterValue.length > 0 ? 1 : 0), 0)

      return listFiltersCount + (this.selectedShowFinished ? 1 : 0)
    },

    sortedUsers() {
      return [...this.users].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },

    jobsSearchedOptions() {
      return this.jobsSearched.filter(j => !j.parent_job_id)
    },

    isListSearchTermTooShort() {
      const term = (this.searchJobTerm || '').trim()

      return term.length > 0 && term.length < this.minSearchLength
    }
  },

  methods: {
    ...mapMutations('app', [
      AppMutations.SET_SELECTED_BUSINESS_APP,
    ]),

    ...mapActions('core/group', [
      GroupActions.GET_ALL_GROUPS_BY_USER_TENANT,
    ]),

    ...mapActions('core/user',
      [UserActions.GET_ALL_USERS]
    ),

    ...mapActions('job', [
      JobActions.SEARCH_JOB,
    ]),

    ...mapMutations('job', [
      JobMutations.ADD_JOB,
      JobMutations.UPDATE_JOB
    ]),

    fetchData() {
      this.loading = true

      const usersOptions = {
        'filter[tenant_id]': this.appConfig.tenant_id,
        'filter[include_foreign]': true,
        'filter[active]': true,
        'limit': 9999,
      }

      const groupsOptions = {
        'filter[tenant]': this.appConfig.tenant_id,
        'filter[include_foreign]': true,
        'filter[active]': true,
        'limit': 9999,
        order: 'group_title',
        direction: 'ASC',
      }

      const poolOptions = {
        'filter[tenant]': this.appConfig.tenant_id,
        'filter[active]': true,
        order: 'pool_title',
        direction: 'ASC',
        offset: 0,
        limit: 2000000000
      }

      const campaignOptions = {
        'filter[tenant]': this.appConfig.tenant_id,
        'filter[active]': true,
        order: 'campaign_title',
        direction: 'ASC',
        offset: 0,
        limit: 2000000000
      }

      const customerOptions = {
        'filter[active]': true,
        order: 'customer_name',
        direction: 'ASC',
        offset: 0,
        limit: 2000000000
      }

      const jobDefinitionOptions = {
        'filter[tenant]': this.appConfig.tenant_id,
        'filter[active]': true,
        order: 'node_id',
        direction: 'DESC',
        offset: 0,
        limit: 9999
      }

      const getUsers = this[UserActions.GET_ALL_USERS](usersOptions)
      getUsers.then((result) => {
        this.users = [...result.items || []]
      })

      const getGroups = this[GroupActions.GET_ALL_GROUPS_BY_USER_TENANT](groupsOptions)
      getGroups.then((result) => {
        this.groups = [...result.items || []]
      })

      const getStatuses = StatusAPI.getAllStatuses({ order: 'status_title', direction: 'ASC', offset: 0, limit: 2000000000 })
      getStatuses.then((statusResult) => {
        this.statuses = [...statusResult.data.items || []]
      })

      const getCampaigns = CampaignAPI.getAllCampaigns(campaignOptions)
      getCampaigns.then((result) => {
        this.campaigns = [...result.data.items || []]
      })

      const getCustomers = CustomerAPI.getAllCustomers(customerOptions)
      getCustomers.then((result) => {
        const data = result?.data
        this.customers = Array.isArray(data) ? data : [...data?.items || []]
      })

      const getJobDefinitions = JobDefinitionAPI.getAllJobDefinitions(jobDefinitionOptions)
      getJobDefinitions.then((result) => {
        this.jobDefinitions = [...result.data.items || []]
      })

      const getJobsInitiator = JobInitiatorAPI.getUserJobsInitiator()
      getJobsInitiator.then((result) => {
        const initiators = [...result.data || []]

        this.jobsInitiator = initiators.map(i => String(i.job_definition_id))
      })

      const getUserLanes = LaneExecutorAPI.getLaneExecutorsByUser()
      getUserLanes.then((result) => {
        this.userLanes = result.data.map(le => le.lane_id)
      })

      const getUserViewAcls = this.loadUserViewAcls()

      let aclsRequest = []
      aclsRequest.push(PoolACLAPI.getPoolACLsFromUserGroup()
        .then((result) => {
          this.userAcls = [...this.userAcls, ...result.data.items || []]
        })
      )

      Promise.all([
        getUsers,
        getGroups,
        getStatuses,
        getCampaigns,
        getCustomers,
        getJobDefinitions,
        getJobsInitiator,
        getUserLanes,
        ...aclsRequest,
        getUserViewAcls
      ])
        .then(async () => {
          await PoolAPI.getAllPools(poolOptions)
            .then(async (result) => {
              let pools = result.data.items || []
              let lanes = []

              pools = pools.filter(p => this.userAcls.find(acl => acl.pool_id == p.pool_id))

              pools.sort((a, b) => a.pool_order - b.pool_order)

              for (const pool of pools) {
                await PoolStatusAPI.getPoolStatusesFromPool(pool.pool_id)
                  .then((poolsStatusesResult) => {
                    pool.statuses = [...poolsStatusesResult.data.items || []]
                  })

                await LaneAPI.getLanesFromPool(pool.pool_id)
                  .then((poolResult) => {
                    const poolLanes = poolResult.data.items || []

                    poolLanes.forEach(lane => {
                      lanes.push({ ...lane, pool: pool, id: lane.lane_id })
                    })
                  })

                const poolAcls = this.userAcls.filter(acl => acl.pool_id == pool.pool_id)

                const ACT_IN_ALL_LANES = 2
                if (poolAcls.some(a => (a.acl & ACT_IN_ALL_LANES) !== 0)) {
                  const poolLaneIds = lanes.filter(l => l.pool.pool_id === pool.pool_id).map(l => l.lane_id)
                  const newLaneIds = poolLaneIds.filter(laneId => !this.userLanes.includes(laneId))
                  this.userLanes = [...this.userLanes, ...newLaneIds]
                }
              }

              this.lanes = [...lanes]
              this.pools = [...pools]
            })
        })
        .catch((error) => {
          // Jobs must not depend on every filter/support request succeeding.
          // For example, a transient customer or ACL request failure on first
          // access must not leave the Job Home timeline empty.
          console.error('Error loading Job Home supporting data:', error)
        })
        .finally(() => {
          this.$refs.timelineView?.fetchJobs()
          this.loading = false
        })
    },

    async loadUserViewAcls() {
      try {
        const result = await UserACLAPI.getUserAclsByUser()
        const data = result?.data || []
        const acls = Array.isArray(data?.items) ? data.items : data

        const aclValue = Array.isArray(acls)
          ? acls.reduce((acc, acl) => acc | (parseInt(acl.acl) || 0), 0)
          : 0

        this.userViewAcls = aclValue
      } catch (e) {
        this.userViewAcls = 0
      } finally {
        this.applyViewAclRestrictions()
      }
    },

    applyViewAclRestrictions() {
      const listAllowed = this.isListViewAllowed
      const timelineAllowed = this.isTimelineViewAllowed

      if (this.view === 'timeline' && !timelineAllowed && listAllowed) {
        this.view = 'list'
      } else if (this.view === 'list' && !listAllowed && timelineAllowed) {
        this.view = 'timeline'
      } else if (this.view !== 'list' && this.view !== 'timeline') {
        this.view = timelineAllowed ? 'timeline' : (listAllowed ? 'list' : this.view)
      }
    },

    goToNow() {
      this.$refs.timelineView.setNewTimelineTime(new Date())
    },

    goToCreateCampaign() {
      // Navigate to campaigns page (no auto-open modal)
      router.push({
        name: 'JobCampaigns',
        params: { appId: this.$route.params.appId }
      })
    },

    goToSettings() {
      // Prevent redundant navigation to the same route
      if (this.$route.name !== 'JobSettings') {
        router.push({
          name: 'JobSettings',
          params: { appId: this.$route.params.appId }
        })
      }
    },

    getStatusById(statusId) {
      return this.statuses.find(s => s.status_id == statusId)
    },

    triggerListSearch() {
      const term = (this.searchJobTerm || '').trim()

      // Elasticsearch requires at least 3 characters to perform a search.
      // Allow empty term so the user can clear the search back to the full list.
      if (term.length > 0 && term.length < this.minSearchLength) {
        return
      }

      this.searchTermEvent = term
    },

    searchJobByTerm(value) {
      // Debounce keystrokes so we don't fire a request per character.
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }

      const term = (value || '').trim()

      // Elasticsearch requires at least 3 characters to perform a search.
      if (term.length < this.minSearchLength) {
        this.jobsSearched = [...[]]
        this.isSearchingJob = false

        return
      }

      this.isSearchingJob = true

      this.searchDebounceTimer = setTimeout(() => {
        this.runJobSearch(term)
      }, 350)
    },

    runJobSearch(term) {
      // Guard against race conditions: only the latest request may update
      // the list. Without this, a slower/earlier request (e.g. for a shorter
      // term while the user was still typing) could overwrite fresher results.
      const requestId = ++this.searchRequestId

      const options = {
        text: term,
        'filter[subtask]': true,
        'filter[nodeType]': 'job_instance'
      }

      this[JobActions.SEARCH_JOB](options)
        .then((result) => {
          if (requestId !== this.searchRequestId) {
            return
          }

          this.jobsSearched = [...result.items || []]
        })
        .finally(() => {
          if (requestId === this.searchRequestId) {
            this.isSearchingJob = false
          }
        })
    },

    handleSelectedJob(job) {
      if (!job) {
        this.filterJobs = [...[]]
        this.goToNow()

        return
      }

      const jobStartDate = new Date(job.start_date)

      const jobIdsToFilter = this.jobsSearched
        .filter(j => j.job_id == job.job_id || j.parent_job_id == job.job_id)
        .map(j => j.job_id)

      this.filterJobs = [...jobIdsToFilter]

      this.$refs.timelineView.setNewTimelineTime(jobStartDate)
    },

    clearFilters() {
      this.selectedSearchedJob = null
      this.filterJobs = [...[]]
      this.selectedCampaign = [...[]]
      this.selectedCustomer = [...[]]
      this.selectedRequester = [...[]]
      this.selectedPool = [...[]]
      this.selectedLane = [...[]]
      this.selectedJobCustomerCode = ""
      this.selectedStatus = [...[]]
      this.selectedPunctuality = [...[]]
      this.selectedShowFinished = false
      this.selectedTimelineInterval = 0
      this.filtersExpanded = false

      // Clear URL parameters
      this.$router.replace({
        query: {}
      }).catch(() => {
        // Ignore navigation duplicated error
      })
    },

    navigateTo(item) {
      router.push({
        name: 'JobDetails',
        params: { jobId: item.id }
      })
    },

    openJobModal(jobDefinitionId) {
      this.selectedJobId = null

      this.$refs.jobModal.openModal(jobDefinitionId)
    },

    viewJob(jobId, mode) {
      this.selectedJobId = jobId
      this.$refs.jobModal.openModal(null, jobId, mode)
    },

    formatSearchedJob(job) {
      if (!job) {
        return ''
      }

      return `#${job.job_id} - ${job.job_title}`
    },

    onJobCreated(jobData) {
      this[JobMutations.ADD_JOB](jobData)
    },

    onJobUpdated(jobData) {
      this[JobMutations.UPDATE_JOB](jobData)
    },

    onJobDeleted(jobId) {
      if (this.$refs.timelineView && this.$refs.timelineView.fetchJobs) {
        console.log("timelineView fetchJobs " + jobId)
        this.$refs.timelineView.fetchJobs()
      }
    },

    parseJson(str) {
      try {
        return JSON.parse(str)
      } catch (e) {
        return {}
      }
    },

    ensureContentAppInRoute() {
      const hasContentParam = !!this.$route.params.contentAppId
      const hasBusinessParam = !!this.$route.params.businessAppId

      // Already carrying both IDs — nothing to do
      if (hasContentParam && hasBusinessParam) return

      const contentAppId = this.appConfig && (this.appConfig.content_app_inst_id || this.appConfig.content_app_id)
      const businessAppId = this.appConfig && (this.appConfig.business_app_inst_id || this.appConfig.business_app_id)

      // Only redirect when at least one app ID is configured
      if (!contentAppId && !businessAppId) return

      // Single redirect carrying both IDs; fall back to 0 (no-op for router guard) when not configured
      this.$router.replace({
        name: 'JobHomeContent',
        params: {
          appId: this.$route.params.appId,
          contentAppId: contentAppId || this.$route.params.contentAppId || 0,
          businessAppId: businessAppId || this.$route.params.businessAppId || 0
        }
      }).catch(() => { })
    },

    loadFiltersFromURL() {
      const query = this.$route.query

      // Load filters from URL query parameters
      if (query.campaigns) {
        this.selectedCampaign = Array.isArray(query.campaigns)
          ? query.campaigns.map(id => parseInt(id))
          : [parseInt(query.campaigns)]
      }

      if (query.statuses) {
        this.selectedStatus = Array.isArray(query.statuses)
          ? query.statuses.map(id => parseInt(id))
          : [parseInt(query.statuses)]
      }

      if (query.customers) {
        this.selectedCustomer = Array.isArray(query.customers)
          ? query.customers.map(id => parseInt(id))
          : [parseInt(query.customers)]
      }

      if (query.requesters) {
        this.selectedRequester = Array.isArray(query.requesters)
          ? query.requesters.map(id => parseInt(id))
          : [parseInt(query.requesters)]
      }

      if (query.pools) {
        this.selectedPool = Array.isArray(query.pools)
          ? query.pools.map(id => parseInt(id))
          : [parseInt(query.pools)]
      }

      if (query.lanes) {
        this.selectedLane = Array.isArray(query.lanes)
          ? query.lanes.map(id => parseInt(id))
          : [parseInt(query.lanes)]
      }

      if (query.jobCustomerCode) {
        this.selectedJobCustomerCode = query.jobCustomerCode
      }

      if (query.punctuality) {
        this.selectedPunctuality = Array.isArray(query.punctuality)
          ? query.punctuality
          : [query.punctuality]
      }

      if (query.showFinished) {
        this.selectedShowFinished = query.showFinished === 'true' || query.showFinished === true
      }

      if (query.timelineInterval) {
        this.selectedTimelineInterval = parseInt(query.timelineInterval)
      }

      if (query.view) {
        this.view = query.view
      }
    },

    updateURLWithFilters() {
      // Start from current query to preserve unknown params (like 'date')
      const query = { ...this.$route.query }

      // Update filters in query (set or remove keys accordingly)
      if (this.selectedCampaign.length > 0) query.campaigns = this.selectedCampaign
      else delete query.campaigns

      if (this.selectedStatusIds.length > 0) query.statuses = this.selectedStatusIds
      else delete query.statuses

      if (this.selectedCustomer.length > 0) query.customers = this.selectedCustomer
      else delete query.customers

      if (this.selectedRequester.length > 0) query.requesters = this.selectedRequester
      else delete query.requesters

      if (this.selectedPool.length > 0) query.pools = this.selectedPool
      else delete query.pools

      if (this.selectedLane.length > 0) query.lanes = this.selectedLane
      else delete query.lanes

      if (this.selectedJobCustomerCode) query.jobCustomerCode = this.selectedJobCustomerCode
      else delete query.jobCustomerCode

      if (this.selectedPunctuality.length > 0) query.punctuality = this.selectedPunctuality
      else delete query.punctuality

      if (this.selectedShowFinished) query.showFinished = true
      else delete query.showFinished

      if (this.selectedTimelineInterval !== 0) query.timelineInterval = this.selectedTimelineInterval
      else delete query.timelineInterval

      query.view = this.view

      // Do not touch 'date' param so deep links keep their anchor

      // Update URL without triggering navigation
      this.$router.replace({ query }).catch(() => { /* ignore duplicate */ })
    },
  },

  watch: {
    userViewAcls() {
      this.applyViewAclRestrictions()
    },

    searchJobTerm(val) {
      this.searchJobByTerm(val)
    },

    selectedSearchedJob(chosenJob) {
      this.handleSelectedJob(chosenJob)
    },

    selectedCampaign: {
      handler() {
        this.updateURLWithFilters()
      },
      deep: true
    },

    selectedStatus: {
      handler() {
        this.updateURLWithFilters()
      },
      deep: true
    },

    selectedCustomer: {
      handler() {
        this.updateURLWithFilters()
      },
      deep: true
    },

    selectedRequester: {
      handler() {
        this.updateURLWithFilters()
      },
      deep: true
    },

    selectedPool: {
      handler() {
        if (this.lanes.length > 0) {
          const availableLaneIds = this.laneFilterOptions.map(lane => String(lane.lane_id))
          this.selectedLane = this.selectedLane.filter(laneId => availableLaneIds.includes(String(laneId)))
        }

        this.updateURLWithFilters()
      },
      deep: true
    },

    selectedLane: {
      handler() {
        this.updateURLWithFilters()
      },
      deep: true
    },

    selectedJobCustomerCode() {
      this.updateURLWithFilters()
    },

    selectedPunctuality: {
      handler() {
        this.updateURLWithFilters()
      },
      deep: true
    },

    view: {
      handler() {
        // Always clear the search term when switching views. All other
        // filters are intentionally preserved.
        this.searchJobTerm = ""
        this.searchTermEvent = ""
        this.selectedSearchedJob = null
        this.jobsSearched = [...[]]

        this.updateURLWithFilters()
      },
      deep: true
    },


    selectedTimelineInterval() {
      this.updateURLWithFilters()
    }
  }
}

</script>
